import React from 'react';
import { BedDouble, Building2, CheckCircle, Church, FileText, House, Landmark, Martini, User, Utensils, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';

interface StructureHeaderProps {
  title?: string;
  activeTab: 'declare' | 'monitor' | 'improve';
  setActiveTab: (tab: 'declare' | 'monitor' | 'improve') => void;
  structureName: string;
  user?: { email?: string } | null;
  onLogout?: () => void;
  onGoHome?: () => void;
  onSwitchStructure?: (slugOrId: string) => void;
  onSwitchToPortal?: () => void;
  structuresList: Array<{ slug?: string; id?: string; name?: string; type?: string; structure?: any }>;
  selectedStructureSlug?: string | null;
  branding?: "visit-bergamo" | "default";
}

export function StructureHeader({
  title = 'Dashboard Gestore Struttura',
  activeTab,
  setActiveTab,
  structureName,
  user,
  onLogout,
  onGoHome,
  onSwitchStructure,
  onSwitchToPortal,
  structuresList,
  selectedStructureSlug,
  branding = "default",
}: StructureHeaderProps) {
  const isVisit = branding === "visit-bergamo";
  const [isStructureMenuHovered, setIsStructureMenuHovered] = React.useState(false);
  const [isLogoutHighlighted, setIsLogoutHighlighted] = React.useState(false);
  const visitPalette = {
    base: "#0f0f0f",
    border: "#1c1c1c",
    accent: "#ec2b3c",
    text: "#ffffff",
    muted: "#dcdcdc",
    light: "#ff0000",
  };
  const travelinPalette = {
    base: "#003c5d",
    border: "rgba(191, 219, 254, 0.3)",
    accent: "#f65009",
    text: "#ffffff",
    muted: "#dbeafe",
    light: "#ffffff",
  };
  const headerPalette = isVisit ? visitPalette : travelinPalette;

  const tabClass = (tab: 'declare' | 'monitor' | 'improve') => {
    if (!isVisit) {
      const active = activeTab === tab;
      return [
        "px-6 py-2.5 rounded-md font-medium transition-all border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        active
          ? "bg-white text-[#f65009] border-white hover:bg-white/95 hover:text-[#f65009] focus:text-[#f65009]"
          : "bg-transparent text-white border-white/35 hover:bg-white/10 hover:text-white hover:border-white"
      ].join(" ");
    }
    const active = activeTab === tab;
    return [
      "px-5 py-2.5 rounded-md font-medium transition-all text-white",
      active
        ? ""
        : "bg-transparent border border-white/12 hover:bg-white/10 hover:text-white hover:border-white/30"
    ].join(" ");
  };

  const tabStyle = (tab: 'declare' | 'monitor' | 'improve') =>
    isVisit && activeTab === tab
      ? { backgroundColor: visitPalette.accent, borderColor: visitPalette.accent }
      : !isVisit && activeTab === tab
      ? { color: travelinPalette.accent }
      : undefined;

  const getStructureTypeIcon = (type?: string) => {
    if (!type) return Building2;

    const normalizedType = type.toLowerCase();

    if (normalizedType.includes('muse')) return Landmark;
    if (normalizedType.includes('church') || normalizedType.includes('chiesa')) return Church;
    if (
      normalizedType.includes('restaurant') ||
      normalizedType.includes('ristor') ||
      normalizedType.includes('food')
    ) {
      return Utensils;
    }
    if (normalizedType.includes('bar') || normalizedType.includes('pub')) return Martini;
    if (
      normalizedType.includes('casa vacanze') ||
      normalizedType.includes('appartamento turistico') ||
      normalizedType.includes('holiday home')
    ) {
      return House;
    }
    if (
      normalizedType.includes('hotel') ||
      normalizedType.includes('bnb') ||
      normalizedType.includes('b&b') ||
      normalizedType.includes('bed and breakfast') ||
      normalizedType.includes('guesthouse') ||
      normalizedType.includes('hostel') ||
      normalizedType.includes('resort') ||
      normalizedType.includes('accommodation') ||
      normalizedType.includes('alloggio')
    ) {
      return BedDouble;
    }

    return Building2;
  };

  const getStructureType = (structure?: { type?: string; structure?: any; document?: any }) => {
    if (!structure) return undefined;

    return (
      structure.document?.structure?.accomodation_data?.type ||
      structure.document?.structure?.accommodation_data?.type ||
      structure.structure?.accomodation_data?.type ||
      structure.structure?.accommodation_data?.type ||
      structure.type
    );
  };

  const selectedStructure = structuresList.find((structure) => {
    if (selectedStructureSlug && structure.slug === selectedStructureSlug) return true;

    const structureDisplayName =
      structure.name ||
      structure.structure?.accomodation_data?.accommodation_name ||
      structure.structure?.accommodation_data?.accommodation_name;

    return structureDisplayName === structureName;
  });

  const selectedStructureType = getStructureType(selectedStructure);
  const SelectedStructureIcon = getStructureTypeIcon(selectedStructureType);

  return (
    <header
      className={isVisit ? "p-4 shadow-lg border-b" : "border-b border-blue-200/30 p-4 shadow-lg"}
      style={{ backgroundColor: headerPalette.base, borderColor: headerPalette.border }}
    >
      <div className="max-w-7xl mx-auto space-y-3">
        <div className="flex justify-center">
          <img
            src={isVisit ? "/visit-bergamo-logo.svg" : "/travelin-logo-dark.svg"}
            alt={isVisit ? "Visit Bergamo" : "Travelin"}
            className="h-10 w-auto object-contain"
          />
        </div>
        <div className="flex items-center justify-between">
          {onGoHome ? (
            <button
              type="button"
              onClick={onGoHome}
              title="Torna alla home"
              className="flex items-center gap-3 group select-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md cursor-pointer"
              style={{ background: 'transparent' }}
            >
              <div className="text-left text-white transition-colors group-hover:text-orange-500">
                <h1 className="text-xl font-semibold leading-tight group-hover:underline">{title}</h1>
                <p className="text-s">
                  {isVisit ? "Visit Bergamo" : "Gestore Struttura"}
                </p>
                <div className="mt-1 flex items-center gap-2 text-ss">
                  <span>Offerto da</span>
                  <img
                    src="/travelin-logo.svg"
                    alt="Travelin"
                    className="h-4 w-auto rounded-sm bg-white px-1 py-0.5"
                  />
                </div>
              </div>
            </button>
          ) : (
            <div className="text-left" style={{ color: headerPalette.text }}>
              <h1 className="text-l font-medium leading-tight">{title}</h1>
              <p className="text-s" style={{ color: isVisit ? visitPalette.text : travelinPalette.muted }}>{isVisit ? "Visit Bergamo" : "Gestore Struttura"}</p>
              <div className="mt-1 flex items-center gap-2 text-ss text-white/80">
                <span>Offerto da</span>
                <img
                  src="/travelin-logo.svg"
                  alt="Travelin"
                  className="h-4 w-auto rounded-sm bg-white px-1 py-0.5"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant={isVisit ? (activeTab === 'monitor' ? 'secondary' : 'outline') : 'outline'}
              onClick={() => setActiveTab('monitor')}
              className={tabClass('monitor')}
              style={tabStyle('monitor')}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Monitora
            </Button>
            <Button
              variant={isVisit ? (activeTab === 'improve' ? 'secondary' : 'outline') : 'outline'}
              onClick={() => setActiveTab('improve')}
              className={tabClass('improve')}
              style={tabStyle('improve')}
            >
              <Wrench className="w-4 h-4 mr-2" />
              Migliora
            </Button>
            <Button
              variant={isVisit ? (activeTab === 'declare' ? 'secondary' : 'outline') : 'outline'}
              onClick={() => setActiveTab('declare')}
              className={tabClass('declare')}
              style={tabStyle('declare')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Dichiara
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-s font-medium text-white">{structureName}</p>
              <p className="text-ss" style={{ color: isVisit ? visitPalette.text : travelinPalette.muted }}>Clicca qui per cambiare struttura</p>
            </div>
            {user && (
              <div className="text-right mr-4 hidden sm:block">
                <p className="text-s font-medium text-white">{user.email}</p>
                <p className="text-ss" style={{ color: isVisit ? visitPalette.text : travelinPalette.muted }}>Accesso effettuato</p>
              </div>
            )}
            <DropdownMenu
              onOpenChange={(open) => {
                if (!open) {
                  setIsStructureMenuHovered(false);
                  setIsLogoutHighlighted(false);
                }
              }}
            >
              <DropdownMenuTrigger className="flex items-center justify-center h-10 w-10 rounded-full p-0 bg-transparent border-0 cursor-pointer hover:bg-white/10">
                <div className={`w-8 h-8 rounded-full ${isVisit ? "bg-white/10 border border-white/15 text-white" : "bg-white text-travelin-blue"} flex items-center justify-center`}>
                  <SelectedStructureIcon className="w-5 h-5" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={`w-56 ${isVisit ? "bg-[#0b0b0b] text-white border border-[#1c1c1c]" : ""}`}
                style={isVisit ? { backgroundColor: "#0b0b0b", borderColor: "#1c1c1c" } : undefined}
                onPointerEnter={() => {
                  if (isVisit) setIsStructureMenuHovered(true);
                }}
                onPointerLeave={() => {
                  if (isVisit) setIsStructureMenuHovered(false);
                }}
              >
                
                <DropdownMenuSeparator />

                <DropdownMenuLabel className={`pb-1 ${isVisit ? "text-white" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>Cambia struttura</span>
                  </div>
                </DropdownMenuLabel>

                {structuresList.map((structure) => (
                  <DropdownMenuItem
                    key={(structure.slug || structure.id) as string}
                    className={`cursor-pointer p-3 ${isVisit ? "group hover:bg-white/10 focus:bg-white/10" : ""} ${selectedStructureSlug === (structure.slug || null) ? (isVisit ? (!isStructureMenuHovered ? 'bg-white/10' : '') : 'bg-accent') : ''}`}
                    onClick={() => {
                      const key = (structure.id || structure.slug) as string;
                      onSwitchStructure && onSwitchStructure(key);
                    }}
                  >
                    {(() => {
                      const structureType = getStructureType(structure);
                      const StructureTypeIcon = getStructureTypeIcon(structureType);

                      return (
                    <div className="flex items-start gap-3 w-full">
                      <div className={`${isVisit ? "w-6 h-6 rounded bg-[#ec2b3c] flex items-center justify-center flex-shrink-0" : "w-6 h-6 rounded bg-gradient-travelin flex items-center justify-center flex-shrink-0"}`}>
                        <StructureTypeIcon className="w-3 h-3 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`text-s font-medium truncate ${isVisit ? "text-white" : ""}`}>
                            {structure.name || structure.structure?.accommodation_data?.accommodation_name}
                          </p>
                          {selectedStructureSlug === (structure.slug || null) && (
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className={`text-ss truncate ${isVisit ? "text-white" : "text-muted-foreground"}`}>
                          {structureType}
                        </p>
                      </div>
                    </div>
                      );
                    })()}
                  </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator />

                {onSwitchToPortal && (
                  <DropdownMenuItem
                    className={`cursor-pointer ${isVisit ? "text-white" : ""}`}
                    onClick={onSwitchToPortal}
                  >
                    <User className="w-4 h-4 mr-3" />
                    <span>Passa alla dashboard portale</span>
                  </DropdownMenuItem>
                )}

                {onLogout && (
                  <DropdownMenuItem
                    onClick={onLogout}
                    className={`cursor-pointer ${isVisit ? "flex justify-center" : "text-red-600"}`}
                    onPointerEnter={() => {
                      if (isVisit) setIsLogoutHighlighted(true);
                    }}
                    onPointerLeave={() => {
                      if (isVisit) setIsLogoutHighlighted(false);
                    }}
                    onFocus={() => {
                      if (isVisit) setIsLogoutHighlighted(true);
                    }}
                    onBlur={() => {
                      if (isVisit) setIsLogoutHighlighted(false);
                    }}
                    style={isVisit
                      ? {
                          backgroundColor: isLogoutHighlighted ? visitPalette.accent : "transparent",
                          color: isLogoutHighlighted ? visitPalette.text : visitPalette.text,
                          border: `1px solid ${isLogoutHighlighted ? visitPalette.light : visitPalette.text}`,
                        }
                      : undefined}
                  >
                    <span>Esci</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
